<template>
    <div class="pathway-filter mb-5">
        <div class="row px-2" v-if="local_census_connection.id == null || local_census_connection.id <= 0">
            <div class="card mb-3">
                <div class="card-body">
                    <h3>Information</h3>
                    <p>Before data can be sent to {{ local_destination.integration.name }}, you need to decide where it will be sent in the remote system and how it will be kept in sync.</p>
                    <div class="w-100 d-flex flex-row gap-3">
                        <div class="w-50">
                            <label>Destination Object</label>
                            <select class="form-select" :value="effectiveDestinationObject" @change="onDestinationObjectChange($event.target.value)"
                            :disabled="local_destination.census_destination_objects.length==1 || local_census_connection.id > 0 || !canEdit('pathways')"
                                :class="{'is-invalid' : errors.destination_object != ''}" aria-label="Destination Object">
                                <option v-for="dest_objects in filteredDestinationObjects" :value="dest_objects.full_name">{{dest_objects.label}}</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.destination_object != ''">
                                {{errors.destination_object}}
                            </div>
                            <div v-if="showConversionActionPicker" class="mt-2">
                                <label class="form-label">Conversion Action</label>
                                <select class="form-select" :value="selectedConversionActionId" @change="onConversionActionChange($event.target.value)"
                                    :disabled="local_census_connection.id > 0 || !canEdit('pathways')"
                                    :class="{'is-invalid' : errors.conversion_action != ''}" aria-label="Conversion Action">
                                    <option value="">Select a conversion action</option>
                                    <option v-for="ca in conversionActions" :key="ca.id" :value="ca.id">{{ ca.name }}</option>
                                </select>
                                <div class="text-danger invalid-feedback" v-if="errors.conversion_action != ''">
                                    {{errors.conversion_action}}
                                </div>
                            </div>
                        </div>
                        <div v-if="operations.length > 1" class="w-50">
                            <label>Method of Sync</label>
                            <select class="form-select" v-model="local_census_connection.operation"
                                :class="{'is-invalid' : errors.operation != ''}" :disabled="local_census_connection.id > 0 || !canEdit('pathways')" aria-label="Method of Sync">
                                <option v-for="ops in operations" :value="ops" >{{getOperationExplanation(ops)}}</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.operation != ''">
                                {{errors.operation}}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row px-2 mb-3">
            <div class="card">
                <div class="card-body">
                    <h3>Limit All Audiences</h3>
                    <p>Set up a filter to determine which records can be included in the sync. This is helpful if you want to restrict the data to certain contact types, email subscriptions, or other criteria. <em>(This can be overridden on a per-audience basis later in the process.)</em></p>
                    <query-builder-simplified 
                        :client="client" 
                        v-model="local_census_connection.filter"
                        :source="local_census_connection.source"
                        :data_source_id="local_census_connection.data_source_id" 
                        ref="filter"
                        :key="'query-builder-pathways'"
                        :if_allow_edit="canEdit('pathways')">
                    </query-builder-simplified>
                </div>
            </div>
        </div>

        <div class="alert alert-danger mb-3" v-if="errors.form">
            There was an issue saving this pathway filter. Please check the fields above.
        </div>

        <div class="row">
            <div class="mb-3 mx-2 d-flex flex-row gap-2">
                <button v-if="canEdit('pathways')" type="submit" class="btn btn-primary save-button" @click="save()" :disabled="busy==true">
                    <span v-if="busy">
                        <div class="spinner-border spinner-border-sm float-left" role="status">
                            <span class="visually-hidden">Saving...</span>
                        </div> 
                        Saving
                    </span>
                    <span v-else>
                        <img class="icon" src="/img/icons/avid-icons/save.svg"> Save
                    </span>
                </button>
                <div v-else class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                    <button class="btn btn-secondary save-button" :disabled="true">
                        <img class="icon" src="/img/icons/avid-icons/save.svg"> Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { router } from '@inertiajs/vue3';
import permissionsMixin from '../../mixins/permissions';

export default {
    props: ['client', 'destination', 'census_connection'],
    mixins: [permissionsMixin],
    data() {
        return {
            local_census_connection: null,
            local_destination: null,
            operations: [],
            destination_fields: [],
            errors: {
                destination_object: "",
                operation: "",
                conversion_action: "",
                form: false
            },
            busy: false,
            conversionActions: []
        };
    },
    computed: {
        effectiveDestinationObject() {
            const obj = this.local_census_connection?.destination_object;
            if (!obj) return obj;
            if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                    return 'enhanced_conversion';
                }
            } catch (_) {}
            return obj;
        },
        showConversionActionPicker() {
            return this.local_destination?.integration?.display_name === 'Google Ads' &&
                this.effectiveDestinationObject === 'enhanced_conversion';
        },
        selectedConversionActionId() {
            const obj = this.local_census_connection?.destination_object;
            if (!obj) return '';
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                return parsed?.conversion_action_id || parsed?.conversionActionId || '';
            } catch (_) {
                return '';
            }
        },
        filteredDestinationObjects() {
            if (!this.local_destination || !this.local_destination.census_destination_objects) {
                return [];
            }
            return this.local_destination.census_destination_objects.filter(obj => obj && obj.data == null);
        }
    },
    beforeMount() {
        this.local_census_connection = this.census_connection;
        this.local_destination = this.destination;

        //Set the column options if the destination has been saved already
        if(this.local_census_connection.destination_object != null){
            const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
            for(var i = 0; i < this.destination.census_destination_objects.length; i++) {
                var obj = this.destination.census_destination_objects[i];
                if(obj.full_name == effectiveObj){
                    this.operations = obj.supported_operations;
                    this.destination_fields = obj.fields;
                    break;
                }
            }
        }
        //If there is only one option
        else if(this.destination.census_destination_objects.length == 1){
            this.local_census_connection.destination_object = this.local_destination.census_destination_objects[0].full_name;
            this.destination_fields = this.destination.census_destination_objects[0].fields;
            this.operations = this.destination.census_destination_objects[0].supported_operations;
            this.changeDestination();
        }

        if(this.local_destination.census_destination_objects == null || this.local_destination.census_destination_objects.length == 0) {
            this.getDestinationObjects();
        }
        if (this.showConversionActionPicker) {
            this.fetchConversionActions();
        }
    },
    methods: {
        getEffectiveDestinationObject(obj) {
            if (!obj) return obj;
            if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                    return 'enhanced_conversion';
                }
            } catch (_) {}
            return obj;
        },
        onDestinationObjectChange(value) {
            if (value === 'enhanced_conversion') {
                this.local_census_connection.destination_object = 'enhanced_conversion';
            } else {
                this.local_census_connection.destination_object = value;
            }
            this.errors.conversion_action = '';
            this.changeDestination();
            if (value === 'enhanced_conversion') {
                this.fetchConversionActions();
            }
        },
        onConversionActionChange(value) {
            if (!value) {
                this.local_census_connection.destination_object = 'enhanced_conversion';
            } else {
                this.local_census_connection.destination_object = JSON.stringify({ conversion_action_id: value });
            }
            this.errors.conversion_action = '';
        },
        fetchConversionActions() {
            if (!this.local_destination?.id) return;
            window.axios.post('/api/census/get_dropdown_options', {
                connection_id: this.local_destination.id,
                column: 'enhanced_conversion.conversion_action'
            }).then(response => {
                this.conversionActions = response.data?.objects || [];
            }).catch(() => {
                this.conversionActions = [];
            });
        },
        getOperationExplanation(ops) {
            if(ops == 'mirror')
                return "Mirror - Records will added, updated, and removed to keep the sources in full sync"
            if(ops == 'append')
                return 'Append - New records will be added to the destination but existing will not be updated or removed';
            if(ops == 'upsert')
                return "Update or Create - Update existing destination records when IDs match, otherwise create new ones if they're missing"
            if(ops == 'update')
                return "Update Only - Matching records will be updated but nothing will be added or removed"
            if(ops == 'delete')
                return "Delete - Delete syncs takes the list of provided IDs and deletes them from the destination. Be careful! Providing an incorrect set of IDs can result in data loss!"
            if(ops == 'create' || ops == 'insert')
                return "Create Only - Create a new object if it doesn't exist in the destination"
        },
        changeDestination() {
            const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
            for(var i = 0; i < this.local_destination.census_destination_objects.length; i++) {
                var obj = this.local_destination.census_destination_objects[i];
                if(obj.full_name == effectiveObj){
                    this.operations = obj.supported_operations;
                    this.destination_fields = obj.fields;
                    break;
                }
            }

            //Now update the operation on the local census connection
            if(this.operations == null){
                this.local_census_connection.operation = null;
                this.operations = [];
                this.destination_fields = [];
            }
            //Set the operation to the first one if it isn't already set
            else if(this.local_census_connection.operation == null){
                this.local_census_connection.operation = this.operations[0];
            }
            //If this.local_census_connection.operation is set and not in the operation array
            else if(this.local_census_connection.operation != null && !this.operations.includes(this.local_census_connection.operation)) {
                this.local_census_connection.operation = this.operations[0];
            }
        },
        getDestinationObjects() {
            var form_data = {
                client_id: this.client.id,
                connection_id: this.local_destination.id
            }
            window.axios.post('/api/census/get_destination_objects', form_data)
              .then(response => {
                //Now I need to set a timeout to check the destination to see when it changes
                if(response.data.objects == null || response.data.objects.length == 0)
                    setTimeout(this.getDestinationObjects, 500);
                else
                    this.local_destination.census_destination_objects = response.data.objects;
              });
        },
        validateCensusConnection() {
            this.errors.operation = "";
            this.errors.destination_object = "";
            this.errors.conversion_action = "";

            let is_valid = true;

            if(this.local_census_connection.operation == null || this.local_census_connection.operation == ''){
                this.errors.operation = 'The method of sync is a required field';
                is_valid = false;
            }
            if(this.local_census_connection.destination_object == null || this.local_census_connection.destination_object == '') {
                is_valid = false;
                this.errors.destination_object = 'The destination object is a required field';
            }
            if (this.effectiveDestinationObject === 'enhanced_conversion' && this.local_destination?.integration?.display_name === 'Google Ads') {
                const caId = this.selectedConversionActionId;
                if (!caId) {
                    is_valid = false;
                    this.errors.conversion_action = 'Please select a conversion action';
                }
            }

            return is_valid;
        },
        save() {
            var is_valid = this.validateCensusConnection();

            // Validate the filter
            if (this.$refs.filter != undefined && !this.$refs.filter.isFormValid()) {
                is_valid = false;
            }

            if (!is_valid) {
                this.errors.form = true;
                return;
            }

            this.errors.form = false;
            this.busy = true;

            let model_id = this.getModelId();

            // Clean up mappings - convert object source to source.value 
            let cleaned_mappings = [];   
            if (this.local_census_connection.mappings) {
                this.local_census_connection.mappings.forEach(mapping => {
                    if (mapping.source_column && typeof mapping.source_column.value === 'object') {
                        mapping.source_column.value = mapping.source_column.value.value;
                    }   
                    if (mapping.destination_column && typeof mapping.destination_column === 'object') {
                        mapping.destination_column = mapping.destination_column.value;
                    }
                    cleaned_mappings.push(mapping);
                });
            }
            this.local_census_connection.mappings = [...cleaned_mappings];

            if (this.local_census_connection.id == null) {
                window.axios.post('/api/census_connection', this.local_census_connection)
                    .then(response => {
                        this.busy = false;
                        Swal.fire({
                            title: "Success!",
                            html: "The filter has been successfully saved. You'll be redirected to the Pathways page momentarily.",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then((result) => {
                            if (document.referrer && document.referrer.includes('pathways')) {
                                window.history.back();
                            } else {
                                router.visit("/pathways/"+this.client.url+"/push_audience");
                            }
                        });
                    });
            } else if (model_id != null) {
                window.axios.put('/api/census_model/' + model_id, this.local_census_connection)
                    .then(response => {
                        this.busy = false;
                        Swal.fire({
                            title: "Success!",
                            html: "Your filter has been successfully updated. You'll be redirected to the Pathways page momentarily.",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then((result) => {
                            if (document.referrer && document.referrer.includes('pathways')) {
                                window.history.back();
                            } else {
                                router.visit("/pathways/"+this.client.url+"/push_audience");
                            }
                        });
                    });
            } else {
                this.local_census_connection.save_mapping = 1;
                window.axios.put('/api/census_connection/' + this.local_census_connection.id, this.local_census_connection)
                    .then(response => {
                        this.busy = false;
                        Swal.fire({
                            title: "Success!",
                            html: "Your filter has been successfully updated. You'll be redirected to the Pathways page momentarily.",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then((result) => {
                            if (document.referrer && document.referrer.includes('pathways')) {
                                window.history.back();
                            } else {
                                router.visit("/pathways/"+this.client.url+"/push_audience");
                            }
                        });
                    });
            }
        },
        getModelId() {
            let urlString = window.location.href;
            const url = new URL(urlString);
            const segments = url.pathname.split('/').filter(segment => segment !== '');
            return segments.length == 7 ? segments[segments.length - 1] : null;
        }
    }
}
</script> 